import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import {
  PLAYBACK_RATES,
  formatPlaybackRate,
  type PlaybackRate,
} from '../../hooks/useVideoPlaybackRate'
import { useI18n } from '../../i18n'

interface PlaybackRateMenuProps {
  /** The current (chosen) rate. */
  rate: number
  onSelect: (rate: PlaybackRate) => void
  /** The owning player is active and on screen; the menu closes when it stops being so. */
  isActive: boolean
  /** Controlled by the owner, which hides the other controls (both clusters) while open. */
  open: boolean
  /** Must be referentially stable (a useState setter). */
  onOpenChange: (open: boolean) => void
}

const stopPropagation = (e: Event) => e.stopPropagation()

/**
 * The playback-speed control: a pill showing the current rate that, while
 * open, covers the media controls with a row of rate chips anchored
 * bottom-right.
 *
 * Every click and pointerdown inside is stopped here, because the video
 * surface toggles play on click and the stories / deck surfaces advance on it.
 */
export function PlaybackRateMenu({
  rate,
  onSelect,
  isActive,
  open,
  onOpenChange,
}: PlaybackRateMenuProps) {
  const { t } = useI18n()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const refocusRef = useRef(false)

  const close = useCallback((refocus: boolean) => {
    if (refocus) refocusRef.current = true
    onOpenChange(false)
  }, [onOpenChange])

  // Refocus the trigger once the owner has actually closed the menu. While it
  // is open the owner hides the trigger (visibility: hidden), so focusing it
  // synchronously from the click / key handler would be a no-op.
  useLayoutEffect(() => {
    if (open || !refocusRef.current) return
    refocusRef.current = false
    triggerRef.current?.focus()
  }, [open])

  // Transition-based on purpose: closes when the player stops being active,
  // not whenever it is inactive (so `open` is deliberately not a dependency).
  useEffect(() => {
    if (!isActive) onOpenChange(false)
  }, [isActive, onOpenChange])

  // Outside press closes. A window capture listener sees presses anywhere,
  // and `composedPath()` sees through the widget's shadow root, where
  // `contains(event.target)` would only see the retargeted host.
  useEffect(() => {
    if (!open) return
    const handleOutside = (e: PointerEvent) => {
      const root = rootRef.current
      if (root && e.composedPath().includes(root)) return
      onOpenChange(false)
    }
    window.addEventListener('pointerdown', handleOutside, true)
    return () => window.removeEventListener('pointerdown', handleOutside, true)
  }, [open, onOpenChange])

  // Move focus into the menu on the checked option when it opens.
  useEffect(() => {
    if (!open) return
    const checked = menuRef.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')
    checked?.focus()
  }, [open])

  const handleTriggerClick = (e: MouseEvent) => {
    e.stopPropagation()
    onOpenChange(!open)
  }

  // Escape on the trigger closes an open menu (e.g. after focus returned to
  // it). A closed menu leaves Escape to the surrounding surface.
  const handleTriggerKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || !open) return
    e.preventDefault()
    e.stopPropagation()
    close(true)
  }

  const handleSelect = (next: PlaybackRate) => (e: MouseEvent) => {
    e.stopPropagation()
    onSelect(next)
    close(true)
  }

  const handleMenuKeyDown = (e: KeyboardEvent) => {
    const options = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]') ?? [],
    )
    if (options.length === 0) return
    const current = options.findIndex((option) => option === e.target)
    let next: number | null = null
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = current < 0 ? 0 : Math.min(current + 1, options.length - 1)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        next = current < 0 ? 0 : Math.max(current - 1, 0)
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = options.length - 1
        break
      case 'Escape':
        e.preventDefault()
        e.stopPropagation()
        close(true)
        return
      case 'Tab':
        onOpenChange(false)
        return
      default:
        // Enter / Space activate the focused chip natively (it is a button).
        return
    }
    e.preventDefault()
    e.stopPropagation()
    options[next]?.focus()
  }

  const label = formatPlaybackRate(rate)

  return (
    <div
      ref={rootRef}
      className="card-media-rate"
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
    >
      {open ? (
        <div
          ref={menuRef}
          className="card-media-rate-menu"
          role="menu"
          aria-label={t('media.playbackSpeed')}
          aria-orientation="horizontal"
          onKeyDown={handleMenuKeyDown}
        >
          {PLAYBACK_RATES.map((option) => {
            const checked = option === rate
            return (
              <button
                key={option}
                type="button"
                className="card-media-rate-option"
                role="menuitemradio"
                aria-checked={checked}
                tabIndex={checked ? 0 : -1}
                onClick={handleSelect(option)}
              >
                {formatPlaybackRate(option)}
              </button>
            )
          })}
        </div>
      ) : null}
      <button
        ref={triggerRef}
        type="button"
        className="card-media-control-btn card-media-rate-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('media.playbackSpeedValue', { rate: label })}
        aria-keyshortcuts="Shift+Comma Shift+Period"
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
      >
        {label}
      </button>
    </div>
  )
}
